import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/api';
import './ApplicationDependencies.css';

interface DispositionCostRecord {
  integration_group_id: string;
  application_id_code: string;
  assessment_planning_cost: number | string;
  design_architecture_cost: number | string;
  migration_execution_cost: number | string;
  testing_validation_cost: number | string;
  golive_optimization_cost: number | string;
  total_cost: number | string;
  application_name?: string;
  group_name?: string;
  last_calculated_date?: string;
  deployment_status?: string;
  justification?: string;
}

interface DeploymentStatusRecord {
  integration_group_id: string;
  application_id: string;
  deployment_status: string;
}

// Status colors
const getStatusColor = (status: string) => {
  switch (status) {
    case 'Need Approval': return { bg: '#fef3c7', color: '#92400e' };
    case 'Approved': return { bg: '#dbeafe', color: '#1e40af' };
    case 'In Development': return { bg: '#e0e7ff', color: '#3730a3' };
    case 'Deployed': return { bg: '#d1fae5', color: '#065f46' };
    default: return { bg: '#f5f5f5', color: '#757575' };
  }
};

// Helper to parse cost values (handle both number and string from API)
const parseCost = (value: number | string | undefined): number => {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') return value;
  return parseFloat(value) || 0;
};

const DispositionCostAnalysis: React.FC = () => {
  const [records, setRecords] = useState<DispositionCostRecord[]>([]);
  const [deploymentStatuses, setDeploymentStatuses] = useState<DeploymentStatusRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterGroupId, setFilterGroupId] = useState('');
  const [filterApplicationId, setFilterApplicationId] = useState('');
  const [recalculating, setRecalculating] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<DispositionCostRecord | null>(null);
  const [justification, setJustification] = useState('');
  const [savingJustification, setSavingJustification] = useState(false);

  // Statistics
  const [stats, setStats] = useState({
    totalApplications: 0,
    totalCost: 0,
    averageCost: 0
  });

  useEffect(() => {
    fetchRecords();
    fetchDeploymentStatuses();

    // Add window focus listener to refresh data when user returns to tab
    const handleFocus = () => {
      fetchRecords();
      fetchDeploymentStatuses();
    };

    // Add visibility change listener to refresh when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchRecords();
        fetchDeploymentStatuses();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    calculateStatistics();
  }, [records]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/disposition-cost-analysis');
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to fetch cost analysis data');
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setRecords(data);
      } else {
        setRecords([]);
      }
      setError('');
    } catch (err: any) {
      console.error('Error fetching disposition cost analysis:', err);
      setRecords([]);
      setError('');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeploymentStatuses = async () => {
    try {
      const response = await authFetch('/api/deployment-status');
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setDeploymentStatuses(data);
        }
      }
    } catch (err: any) {
      console.error('Error fetching deployment statuses:', err);
    }
  };

  const getDeploymentStatus = (groupId: string, appId: string): string => {
    const status = deploymentStatuses.find(
      s => s.integration_group_id === groupId && s.application_id === appId
    );
    return status?.deployment_status || '-';
  };

  const calculateStatistics = () => {
    const totalApps = records.length;
    const total = records.reduce((sum, r) => sum + parseCost(r.total_cost), 0);
    const avg = totalApps > 0 ? total / totalApps : 0;

    setStats({
      totalApplications: totalApps,
      totalCost: total,
      averageCost: avg
    });
  };

  const handleViewDetails = (record: DispositionCostRecord) => {
    setSelectedRecord(record);
    setJustification(record.justification || '');
  };

  const handleSaveJustification = async () => {
    if (!selectedRecord) return;
    setSavingJustification(true);
    try {
      const response = await authFetch(`/api/disposition-cost-analysis/${selectedRecord.application_id_code}/justification`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ justification })
      });
      if (!response.ok) throw new Error('Failed to save');
      setRecords(prev => prev.map(r =>
        r.application_id_code === selectedRecord.application_id_code
          ? { ...r, justification } : r
      ));
      setSelectedRecord(null);
      setSuccess('Justification saved successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to save justification');
    } finally {
      setSavingJustification(false);
    }
  };

  const recalculateCosts = async (applicationId: string) => {
    setRecalculating(true);
    setError('');
    setSuccess('');

    try {
      const response = await authFetch(`/api/disposition-cost-analysis/calculate/${applicationId}`, {
        method: 'POST'
      });

      if (!response.ok) throw new Error('Failed to calculate costs');

      const result = await response.json();
      setSuccess(`Successfully recalculated costs for ${applicationId}`);
      fetchRecords();
    } catch (err: any) {
      setError(err.message || 'Failed to recalculate costs');
    } finally {
      setRecalculating(false);
    }
  };

  const recalculateAllCosts = async () => {
    if (!window.confirm('This will recalculate costs for all applications with effort planning data. Continue?')) {
      return;
    }

    setRecalculating(true);
    setError('');
    setSuccess('');

    try {
      const response = await authFetch('/api/disposition-cost-analysis/calculate-all', {
        method: 'POST'
      });

      if (!response.ok) throw new Error('Failed to calculate costs');

      const result = await response.json();

      let message = `Successfully processed ${result.applications_found || 0} application(s).`;
      if (result.processed > 0) {
        message += ` Calculated costs for ${result.processed} application(s).`;
      }
      if (result.skipped > 0) {
        message += ` Skipped ${result.skipped} application(s).`;
      }

      setSuccess(message);
      fetchRecords();
    } catch (err: any) {
      setError(err.message || 'Failed to recalculate all costs');
    } finally {
      setRecalculating(false);
    }
  };

  const filteredRecords = records.filter(record => {
    // App search works independently — if searching, show matches across all groups
    if (filterApplicationId.trim()) {
      const search = filterApplicationId.toLowerCase();
      const matchesApp = (record.application_id_code || '').toLowerCase().includes(search) ||
        (record.application_name || '').toLowerCase().includes(search);
      // If a group is also selected, filter within that group
      if (filterGroupId && filterGroupId !== '__ALL__') {
        return matchesApp && record.integration_group_id === filterGroupId;
      }
      return matchesApp;
    }
    // No app search — require group selection
    if (!filterGroupId) return false;
    return filterGroupId === '__ALL__' || record.integration_group_id === filterGroupId;
  });

  const formatCurrency = (amount: number | string): string => {
    const numAmount = parseCost(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numAmount);
  };

  const exportToCSV = () => {
    const headers = [
      'Integration Group ID',
      'Group Name',
      'Application ID',
      'Application Name',
      'Assessment & Planning Cost',
      'Design & Architecture Cost',
      'Migration Execution Cost',
      'Testing & Validation Cost',
      'GoLive & Optimization Cost',
      'Total Cost',
      'Justification'
    ];

    const rows = filteredRecords.map(record => [
      record.integration_group_id,
      record.group_name || '',
      record.application_id_code,
      record.application_name || '',
      parseCost(record.assessment_planning_cost),
      parseCost(record.design_architecture_cost),
      parseCost(record.migration_execution_cost),
      parseCost(record.testing_validation_cost),
      parseCost(record.golive_optimization_cost),
      parseCost(record.total_cost),
      `"${(record.justification || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `disposition_cost_analysis_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="loading">Loading disposition cost analysis...</div>;
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Disposition Cost Analysis</h1>
            <p className="page-header__subtitle">
              Phase-based cost calculation for application migration based on duration, FTE allocation, and daily rates
            </p>
          </div>
          <div className="page-header__actions">
            <div
              title="Total Applications"
              style={{
                padding: '8px 16px',
                backgroundColor: '#e3f2fd',
                border: '2px solid #1976d2',
                borderRadius: '6px',
                fontWeight: '600',
                color: '#1565c0',
                fontSize: '14px'
              }}
            >
              Total Apps: {stats.totalApplications}
            </div>
            <div
              title="Total Cost"
              style={{
                padding: '8px 16px',
                backgroundColor: '#fff3e0',
                border: '2px solid #f57c00',
                borderRadius: '6px',
                fontWeight: '600',
                color: '#e65100',
                fontSize: '14px'
              }}
            >
              Total: {formatCurrency(stats.totalCost)}
            </div>
            <div
              title="Average Cost per Application"
              style={{
                padding: '8px 16px',
                backgroundColor: '#e8f5e9',
                border: '2px solid #4caf50',
                borderRadius: '6px',
                fontWeight: '600',
                color: '#2e7d32',
                fontSize: '14px'
              }}
            >
              Avg: {formatCurrency(stats.averageCost)}
            </div>
            <button
              onClick={exportToCSV}
              className="btn btn-outline-success"
              title="Download current data as CSV"
              style={{ border: '2px solid #28a745' }}
            >
              📥 Download Data
            </button>
            <button
              onClick={fetchRecords}
              disabled={loading}
              className="btn btn-secondary"
              title="Refresh data from server"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <span style={{ fontSize: '16px' }}>🔄</span>
              {loading ? 'Refreshing...' : 'Refresh Data'}
            </button>
            <button
              onClick={recalculateAllCosts}
              disabled={recalculating}
              className="btn btn-primary"
              title="Recalculate costs for all applications"
            >
              {recalculating ? 'Recalculating...' : 'Recalculate All'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="master-data-container">

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">!</span>
            {error}
          </div>
        )}

        {/* Filter Section */}
        <div className="md-card" style={{ marginBottom: '16px' }}>
          <div className="md-card-content" style={{ padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '16px', fontWeight: '500', color: '#374151' }}>
                  Grouping ID:
                </label>
                <select
                  value={filterGroupId}
                  onChange={(e) => setFilterGroupId(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '16px',
                    minWidth: '300px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select Grouping</option>
                  <option value="__ALL__">All Grouping IDs</option>
                  {Array.from(new Set(records.map(r => r.integration_group_id).filter(Boolean))).sort().map(groupId => (
                    <option key={groupId} value={groupId}>{groupId}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '16px', fontWeight: '500', color: '#374151' }}>
                  Application:
                </label>
                <input
                  type="text"
                  placeholder="🔍 Search by App ID or name..."
                  value={filterApplicationId}
                  onChange={(e) => setFilterApplicationId(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    minWidth: '250px',
                    outline: 'none'
                  }}
                />
              </div>
              {(filterGroupId || filterApplicationId) && (
                <button
                  onClick={() => {
                    setFilterGroupId('');
                    setFilterApplicationId('');
                  }}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#f1f5f9',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#374151'
                  }}
                >
                  Clear Filters
                </button>
              )}
              <span style={{ fontSize: '14px', color: '#6b7280', marginLeft: 'auto' }}>
                {!filterGroupId ?
                  `Select a Grouping to view data (${records.length} total records)` :
                  `Showing ${filteredRecords.length} of ${records.length} records`
                }
              </span>
            </div>
          </div>
        </div>

        {/* Cost Analysis Table */}
        <div className="md-card">
          {records.length === 0 ? (
            <div className="md-empty-state">
              <div className="md-empty-icon">$</div>
              <h3>No Cost Analysis Data Available</h3>
              <p>Run cost calculations to populate this table.</p>
              <button
                onClick={recalculateAllCosts}
                className="md-button md-button--primary"
                style={{ marginTop: '16px' }}
              >
                Calculate Costs
              </button>
            </div>
          ) : (
            <div className="md-table-container">
              <table className="md-table" style={{ fontSize: '11px' }}>
                <thead>
                  <tr>
                    <th rowSpan={2} style={{ textAlign: 'center', padding: '8px', backgroundColor: '#f3f4f6', position: 'sticky', left: 0, zIndex: 2, minWidth: '180px', width: '180px' }}>
                      Integration<br/>Grouping ID
                    </th>
                    <th rowSpan={2} style={{ textAlign: 'center', padding: '8px', backgroundColor: '#f3f4f6', position: 'sticky', left: '180px', zIndex: 2, minWidth: '180px', width: '180px' }}>
                      Application<br/>ID
                    </th>
                    <th rowSpan={2} style={{ textAlign: 'center', padding: '8px', backgroundColor: '#f3f4f6' }}>
                      Deployment<br/>Status
                    </th>
                    <th colSpan={5} style={{ textAlign: 'center', padding: '8px', backgroundColor: '#e5e7eb' }}>
                      Phase Costs (USD)
                    </th>
                    <th rowSpan={2} style={{ textAlign: 'center', padding: '8px', backgroundColor: '#f3f4f6' }}>
                      Total Cost<br/>(USD)
                    </th>
                    <th rowSpan={2} style={{ textAlign: 'center', padding: '8px', backgroundColor: '#f3f4f6', minWidth: '60px' }}>
                      Details
                    </th>
                  </tr>
                  <tr>
                    <th style={{ textAlign: 'center', padding: '6px', backgroundColor: '#f9fafb', fontSize: '10px' }}>
                      Assessment &<br/>Planning
                    </th>
                    <th style={{ textAlign: 'center', padding: '6px', backgroundColor: '#f9fafb', fontSize: '10px' }}>
                      Design &<br/>Architecture
                    </th>
                    <th style={{ textAlign: 'center', padding: '6px', backgroundColor: '#f9fafb', fontSize: '10px' }}>
                      Migration<br/>Execution
                    </th>
                    <th style={{ textAlign: 'center', padding: '6px', backgroundColor: '#f9fafb', fontSize: '10px' }}>
                      Testing &<br/>Validation
                    </th>
                    <th style={{ textAlign: 'center', padding: '6px', backgroundColor: '#f9fafb', fontSize: '10px' }}>
                      GoLive &<br/>Optimization
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => (
                    <tr key={`${record.integration_group_id}-${record.application_id_code}`}>
                      <td style={{
                        padding: '6px',
                        backgroundColor: '#fff',
                        position: 'sticky',
                        left: 0,
                        borderRight: '1px solid #e5e7eb',
                        minWidth: '180px',
                        width: '180px'
                      }}>
                        <div>
                          <div style={{ fontWeight: '500' }}>{record.integration_group_id}</div>
                          {record.group_name && (
                            <div style={{ fontSize: '9px', color: '#666' }}>{record.group_name}</div>
                          )}
                        </div>
                      </td>
                      <td style={{
                        padding: '6px',
                        backgroundColor: '#fff',
                        position: 'sticky',
                        left: '180px',
                        borderRight: '1px solid #e5e7eb',
                        minWidth: '180px',
                        width: '180px'
                      }}>
                        <div>
                          <div style={{ fontWeight: '500' }}>{record.application_id_code}</div>
                          {record.application_name && (
                            <div style={{ fontSize: '9px', color: '#666' }}>{record.application_name}</div>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center', padding: '6px' }}>
                        {(() => {
                          const status = getDeploymentStatus(record.integration_group_id, record.application_id_code);
                          const statusColor = getStatusColor(status);
                          return status !== '-' ? (
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: '500',
                              backgroundColor: statusColor.bg,
                              color: statusColor.color
                            }}>
                              {status}
                            </span>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '10px' }}>-</span>
                          );
                        })()}
                      </td>
                      <td style={{ textAlign: 'right', padding: '6px' }}>
                        {formatCurrency(record.assessment_planning_cost)}
                      </td>
                      <td style={{ textAlign: 'right', padding: '6px' }}>
                        {formatCurrency(record.design_architecture_cost)}
                      </td>
                      <td style={{ textAlign: 'right', padding: '6px' }}>
                        {formatCurrency(record.migration_execution_cost)}
                      </td>
                      <td style={{ textAlign: 'right', padding: '6px' }}>
                        {formatCurrency(record.testing_validation_cost)}
                      </td>
                      <td style={{ textAlign: 'right', padding: '6px' }}>
                        {formatCurrency(record.golive_optimization_cost)}
                      </td>
                      <td style={{
                        textAlign: 'right',
                        padding: '6px',
                        fontWeight: '600',
                        backgroundColor: '#fef3c7'
                      }}>
                        {formatCurrency(record.total_cost)}
                      </td>
                      <td style={{ textAlign: 'center', padding: '6px' }}>
                        <button
                          onClick={() => handleViewDetails(record)}
                          style={{
                            padding: '3px 8px', fontSize: '11px',
                            border: '1px solid #1976d2', borderRadius: '4px',
                            backgroundColor: 'transparent', cursor: 'pointer',
                            color: '#1976d2', display: 'inline-flex', alignItems: 'center', gap: '3px'
                          }}
                          title="View cost details and justification"
                        >
                          <span>👁️</span> <span>View</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {filteredRecords.length > 0 && (
                  <tfoot>
                    <tr style={{ backgroundColor: '#f3f4f6', fontWeight: '600' }}>
                      <td colSpan={3} style={{ padding: '8px', textAlign: 'right' }}>
                        TOTALS:
                      </td>
                      <td style={{ textAlign: 'right', padding: '8px' }}>
                        {formatCurrency(filteredRecords.reduce((sum, r) => sum + parseCost(r.assessment_planning_cost), 0))}
                      </td>
                      <td style={{ textAlign: 'right', padding: '8px' }}>
                        {formatCurrency(filteredRecords.reduce((sum, r) => sum + parseCost(r.design_architecture_cost), 0))}
                      </td>
                      <td style={{ textAlign: 'right', padding: '8px' }}>
                        {formatCurrency(filteredRecords.reduce((sum, r) => sum + parseCost(r.migration_execution_cost), 0))}
                      </td>
                      <td style={{ textAlign: 'right', padding: '8px' }}>
                        {formatCurrency(filteredRecords.reduce((sum, r) => sum + parseCost(r.testing_validation_cost), 0))}
                      </td>
                      <td style={{ textAlign: 'right', padding: '8px' }}>
                        {formatCurrency(filteredRecords.reduce((sum, r) => sum + parseCost(r.golive_optimization_cost), 0))}
                      </td>
                      <td style={{ textAlign: 'right', padding: '8px', backgroundColor: '#fef3c7' }}>
                        {formatCurrency(filteredRecords.reduce((sum, r) => sum + parseCost(r.total_cost), 0))}
                      </td>
                      <td style={{ padding: '8px' }}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>

      </div>

      {/* Cost Details & Justification Modal */}
      {selectedRecord && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '600px', width: '90%', maxHeight: '80vh', overflow: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <h2 style={{ margin: '0 0 16px 0', color: '#1976d2', fontSize: '18px' }}>
              Disposition Cost Details
            </h2>

            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>{selectedRecord.application_id_code} — {selectedRecord.application_name}</div>
              <div style={{ fontSize: '13px', color: '#666' }}>Integration Group: {selectedRecord.integration_group_id} {selectedRecord.group_name && `— ${selectedRecord.group_name}`}</div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginBottom: '16px' }}>
              <tbody>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px 0', color: '#666' }}>Assessment & Planning</td>
                  <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(selectedRecord.assessment_planning_cost)}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px 0', color: '#666' }}>Design & Architecture</td>
                  <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(selectedRecord.design_architecture_cost)}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px 0', color: '#666' }}>Migration Execution</td>
                  <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(selectedRecord.migration_execution_cost)}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px 0', color: '#666' }}>Testing & Validation</td>
                  <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(selectedRecord.testing_validation_cost)}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px 0', color: '#666' }}>Go-Live & Optimization</td>
                  <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(selectedRecord.golive_optimization_cost)}</td>
                </tr>
                <tr style={{ borderTop: '2px solid #333' }}>
                  <td style={{ padding: '8px 0', fontWeight: 700 }}>Total Disposition Cost</td>
                  <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 700, color: '#1976d2' }}>{formatCurrency(selectedRecord.total_cost)}</td>
                </tr>
              </tbody>
            </table>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px' }}>
                Justification / Notes
              </label>
              <textarea
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                placeholder="Enter justification or notes for this disposition cost..."
                style={{
                  width: '100%', minHeight: '100px', padding: '10px',
                  border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px',
                  resize: 'vertical', boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedRecord(null)}
                style={{ padding: '8px 16px' }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveJustification}
                disabled={savingJustification}
                style={{ padding: '8px 16px' }}
              >
                {savingJustification ? 'Saving...' : 'Save Justification'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DispositionCostAnalysis;
